import {match} from 'ts-pattern';

import type {ColumnResponseBody} from '../types/Api';
import type {EagColumn, EagFilterDefinition} from '../types/EagColumn';

type ColumnFilter = NonNullable<ColumnResponseBody['filter']>;

const mapQuickFilters = (qfs: Array<{value: string; label: string}> | undefined) =>
  qfs?.map((qf) => ({value: qf.value, label: qf.label}));

const mapFilterToEagFilterDefinition = (filter: ColumnFilter): EagFilterDefinition | null =>
  match(filter)
    .with({type: 'TEXT'}, (f) => ({
      type: 'TEXT' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    .with({type: 'SWITCH'}, (f) => ({
      type: 'SWITCH' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    .with({type: 'DATE'}, (f) => ({
      type: 'DATE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      relativeDates: f.relativeDates?.map((rd) => ({value: rd.value, label: rd.label})),
    }))
    .with({type: 'DATE_RANGE'}, (f) => ({
      type: 'DATE_RANGE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      relativeDates: f.relativeDates?.map((rd) => ({value: rd.value, label: rd.label})),
      relativeInputs: f.relativeInputs?.map((ri) => ({value: ri.value, label: ri.label})),
    }))
    .with({type: 'DATE_TIME_RANGE'}, (f) => ({
      type: 'DATE_TIME_RANGE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
    }))
    .with({type: 'MULTI_CHOICE'}, (f) => ({
      type: 'MULTI_CHOICE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      options: f.options?.map((opt) => ({key: opt.key, label: opt.label})),
    }))
    .with({type: 'MULTI_CHOICE_ASYNC'}, (f) => ({
      type: 'MULTI_CHOICE_ASYNC' as const,
      isDisabled: f.isDisabled,
      suggestId: f.suggestId,
    }))
    .with({type: 'CHIPS'}, (f) => ({
      type: 'CHIPS' as const,
      isDisabled: f.isDisabled,
      options: f.options?.map((opt) => ({key: opt.key, label: opt.label})),
    }))
    .with({type: 'CHECKBOX'}, (f) => ({
      type: 'CHECKBOX' as const,
      isDisabled: f.isDisabled,
    }))
    .with({type: 'SLIDER'}, (f) => ({
      type: 'SLIDER' as const,
      isDisabled: f.isDisabled,
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      stepSize: f.stepSize ?? undefined,
      unit: f.unit ?? undefined,
    }))
    .with({type: 'SLIDER_RANGE'}, (f) => ({
      type: 'SLIDER_RANGE' as const,
      isDisabled: f.isDisabled,
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      stepSize: f.stepSize ?? undefined,
      unit: f.unit ?? undefined,
    }))
    .with({type: 'RANGE'}, (f) => ({
      type: 'RANGE' as const,
      isDisabled: f.isDisabled,
    }))
    .otherwise(() => null);

export const columnToFilterDefs = (column: ColumnResponseBody): Partial<EagColumn> => {
  if (!column.filter) {
    return {};
  }

  const filterDef = mapFilterToEagFilterDefinition(column.filter);

  if (!filterDef) {
    return {};
  }

  return {filter: filterDef};
};
